// Fax drafts — a half-written "Send a fax" or "Request documents" kept in the
// database so closing the window never loses it. Shared: every coordinator
// sees every draft. See supabase/migrations/*_fax_drafts.sql.
//
// Files uploaded from a computer are staged in Drive by send-fax and referred
// to here by id; binning them always goes through send-fax, which refuses to
// touch anything outside the staging folder.
import { supabase } from './supabase';

// date_of_birth and medicaid_id: a resumed draft's cover page prints them.
const CLIENT_COLS = 'clients(id, client_id, first_name, last_name, date_of_birth, medicaid_id)';

export const DRAFT_KIND_LABEL = { general: 'Send a fax', request: 'Request documents' };

export const listFaxDrafts = () =>
  supabase.from('fax_drafts').select(`*, ${CLIENT_COLS}`).order('updated_at', { ascending: false });

export const listClientFaxDrafts = (clientId) =>
  supabase.from('fax_drafts').select('*').eq('client_id', clientId).order('updated_at', { ascending: false });

export const getFaxDraft = (id) =>
  supabase.from('fax_drafts').select(`*, ${CLIENT_COLS}`).eq('id', id).maybeSingle();

// Insert when there is no id yet, update otherwise. Returns the row.
export async function saveFaxDraft(id, fields, profile) {
  const who = { updated_by: profile?.id || null, updated_by_name: profile?.full_name || null };
  if (id) {
    const { data, error } = await supabase.from('fax_drafts').update({ ...fields, ...who }).eq('id', id).select('id, updated_at').single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase.from('fax_drafts')
    .insert({ ...fields, ...who, created_by: profile?.id || null, created_by_name: profile?.full_name || null })
    .select('id, updated_at').single();
  if (error) throw error;
  return data;
}

async function callSendFax(body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-fax`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false || json.error) throw new Error(json.error || `send-fax failed (${res.status})`);
  return json;
}

// Park an uploaded file in Drive so a draft can keep it. → { drive_file_id, name }
export const stageFaxFile = (name, base64) => callSendFax({ action: 'stage_file', name, base64 });

// Bin staged files the user took back out of a draft.
export const unstageFaxFiles = (ids) => ids.length ? callSendFax({ action: 'bin_staged', drive_file_ids: ids }) : Promise.resolve({ binned: 0 });

// Delete a draft and bin everything it staged.
export const discardFaxDraft = (id) => callSendFax({ action: 'discard_draft', draft_id: id });
